"use client";
import React, { useEffect, useState, useMemo } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import { useAuth } from '../../api/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProgramsTable from './ProgramsTable';
import NewProgramModal from './NewProgramModal';
import LoaderOverlay from '../../components/LoaderOverlay';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import { getPrograms, createProgram, updateProgram, deleteProgram, getProgram } from '../../api/programs';
import { getInstitutions, getSections, getPeriodTypes, getCycles, getCampusOptions } from '../../api/catalogs';
import Toast from '../DocentesScreen/Toast';
import { useSidebar } from '../../components/Sidebar';
import { usePermissions } from '../../hooks/usePermissions';

const PantallaDeProgramasAcademicos = () => {
  const { isCollapsed } = useSidebar();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' | 'view' | 'edit'
  const [query, setQuery] = useState("");
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // objeto del backend (list)

  // PERMISOS
  const { canCreate, canEdit, canView } = usePermissions();
  const puedeCrear =
    canCreate('Gestión Escolar') ||
    canEdit('Gestión Escolar') ||
    canView('Gestión Escolar');

  // Catálogos precargados
  const [institutions, setInstitutions] = useState([]);
  const [sections, setSections] = useState([]);
  const [periodTypes, setPeriodTypes] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [campuses, setCampuses] = useState([]);

  // Precargar catálogos al montar
  useEffect(() => {
    let mounted = true;
    const loadCatalogs = async () => {
      try {
        const inst = await getInstitutions();
        if (mounted) setInstitutions(inst);
        // Usar la primera institución como default para dependientes
        const instId = inst[0]?.id;
        if (!instId) return;

        // Cargar catálogos dependientes en paralelo para reducir el tiempo total
        const [sec, pts, cyc, camp] = await Promise.all([
          getSections(instId),
          getPeriodTypes(instId),
          getCycles(instId),
          getCampusOptions(instId),
        ]);

        if (mounted) {
          setSections(sec);
          setPeriodTypes(pts);
          setCycles(cyc);
          setCampuses(camp);
        }
      } catch (e) {
        // Opcional: mostrar error
      }
    };
    loadCatalogs();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await getPrograms();
        if (mounted) setPrograms(data);
      } catch (e) {
        console.error('Error cargando programas:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    // Escuchar eventos de actualización de programas
    const handler = (e) => {
      if (Array.isArray(e.detail)) setPrograms(e.detail);
    };
    window.addEventListener('programs:updated', handler);
    return () => {
      mounted = false;
      window.removeEventListener('programs:updated', handler);
    };
  }, []);

  // Filtrado en tiempo real de programas (similar a asesores)
  const filteredPrograms = useMemo(() => {
    if (!query.trim()) return programs;

    const term = query.toLowerCase().trim();
    return programs.filter((p) => {
      const name = (p.name || p.nombre_programa || '').toLowerCase();
      const code = (p.code || p.codigo_programa || p.program_code || '').toLowerCase();
      const section = (p.section || p.seccion_nombre || '').toLowerCase();
      const institution = (p.institutionName || p.institucion_nombre || '').toLowerCase();

      return (
        name.includes(term) ||
        code.includes(term) ||
        section.includes(term) ||
        institution.includes(term)
      );
    });
  }, [programs, query]);

  const handleSearch = () => {
    // El filtrado ya se aplica en tiempo real con `query`
    console.log('Buscando programas (filtro local) con término:', query);
  };
  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <HeaderGlobal
            title="Programas Académicos"/>
          <main className={styles.mainContent}>
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Cargando programas..." />
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow}>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar Programa Académico..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSearch();
                          }}
                        />
                      </div>

                      <button
                        className={styles.buscarButtonProgramas}
                        onClick={handleSearch}
                      >
                        Buscar
                      </button>

                      {puedeCrear && (
                        <button
                          className={styles.newProgramButton}
                          onClick={() => setOpen(true)}
                        >
                          + Nuevo Programa
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <ProgramsTable
                  query={query}
                  programs={filteredPrograms}
                  loading={loading}
                  onView={async (p) => {
                    const id = p.id || p.id_programa;
                    if (!id) return window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de programa no válido' } }));
                    try {
                      const full = await getProgram(id);
                      setSelected(full);
                      setMode('view');
                      setOpen(true);
                    } catch (e) {
                      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo cargar el programa' } }));
                    }
                  }}
                  onEdit={async (p) => {
                    const id = p.id || p.id_programa;
                    if (!id) return window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de programa no válido' } }));
                    try {
                      const full = await getProgram(id);
                      setSelected(full);
                      setMode('edit');
                      setOpen(true);
                    } catch (e) {
                      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo cargar el programa' } }));
                    }
                  }}
                  onDelete={async (p) => {
                    const id = p.id || p.id_programa;
                    if (!id) return window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de programa no válido' } }));
                    if (!window.confirm(`¿Eliminar el programa "${p.name}"?`)) return;
                    try {
                      setLoading(true);
                      const updated = await deleteProgram(id);
                      // La API ya recarga la lista completa de programas
                      setPrograms(updated);
                      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Programa eliminado' } }));
                    } catch (e) {
                      console.error('Error eliminando programa:', e);
                      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo eliminar el programa' } }));
                    } finally {
                      setLoading(false);
                    }
                  }}
                />
              </div>
            </div>
          </main>
        </div>
      </div>
      <NewProgramModal
        open={open}
        mode={mode}
        onClose={() => { setOpen(false); setSelected(null); setMode('create'); }}
        readOnly={mode === 'view'}
        initialData={selected ? {
          id: selected.id_programa || selected.id,
          name: selected.nombre_programa || selected.name || '',
          program_code: selected.program_code || selected.codigo || '',
          code: selected.codigo_programa || selected.code || selected.clave_programa || '',
          rvoe: selected.numero_revoe || selected.rvoe || '',
          institution: String(selected.id_institucion?.id_institucion || selected.id_institucion || ''),
          campus: String(selected.campus_id || selected.campus || ''),
          periodTypeId: String(selected.id_tipo_periodo?.id_tipo_periodo || selected.id_tipo_periodo || ''),
          periodType: selected.tipo_periodo_name || selected.tipo_periodo || selected.periodType || selected.period || '',
          section: String(selected.id_seccion?.id_seccion || selected.id_seccion || ''),
          duration: selected.duracion_periodos || selected.duration || '',
          credits: String(selected.creditos_totales ?? selected.credits ?? ''),
          description: selected.descripcion || selected.description || '',
          objectives: selected.objetivos || selected.objectives || '',
          profile: selected.perfil_egresado || selected.profile || '',
          rvoeDate: selected.fecha_registro_revoe || selected.rvoeDate || '',
          cycleId: selected.ciclo_inicio || selected.cycleId || '',
          modalidad: selected.modalidad || '',
          status: selected.activo === false ? 'INACTIVO' : 'ACTIVO',
        } : undefined}
        institutions={institutions}
        sections={sections}
        periodTypes={periodTypes}
        cycles={cycles}
        campuses={campuses}
        onSubmit={async (form) => {
          try {
            setLoading(true);
            // Usar id_programa del backend o el id del objeto selected
            const programId = selected?.id_programa || selected?.id;
            const updated = mode === 'edit'
              ? await updateProgram(programId, form)
              : await createProgram(form);
            setPrograms(updated);
            window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: mode === 'edit' ? 'Programa actualizado' : 'Programa creado' } }));
            // Cerrar modal y resetear estado después de guardar exitosamente
            setOpen(false);
            setSelected(null);
            setMode('create');
          } catch (e) {
            console.error('Error guardando programa:', e);
            window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: `No se pudo guardar el programa. ${e?.message || ''}` } }));
          } finally {
            setLoading(false);
          }
        }}
      />
      <Toast />
    </div>
  );
};

export default PantallaDeProgramasAcademicos;
